"use client";

import { createApi } from "@reduxjs/toolkit/query/react";

import {
  LoginPayloadModel,
  RestorePasswordModel,
  ResetPasswordUserInfoResponse,
  UserDataModel,
} from "@/libs/auth/redux/auth.types";
import baseQuery from "@/libs/shared/api/baseQuery";
import {
  CompleteSignupModel,
  SignupResponse,
  SignupStartModel,
} from "@/libs/auth/redux/signup.types";
import { objectToFormData } from "@/libs/shared/api/objectToFormData";

// Define a service using a base URL and expected endpoints
export const authApi = createApi({
  reducerPath: "authApi",
  baseQuery,
  endpoints: (builder) => ({
    login: builder.mutation<UserDataModel, LoginPayloadModel>({
      query: (credentials) => ({
        url: "login",
        method: "POST",
        body: {
          ...credentials,
          language: "it",
        },
      }),
    }),
    register: builder.mutation<SignupResponse, SignupStartModel>({
      query: ({ gdpr_terms, ...body }: SignupStartModel) => {
        return {
          url: "register",
          method: "POST",
          body: {
            ...body,
            gdpr: gdpr_terms,
            terms: gdpr_terms,
          },
        };
      },
    }),
    completeRegistration: builder.mutation<UserDataModel, CompleteSignupModel>({
      query: (payload: CompleteSignupModel) => {
        const formData = objectToFormData(payload);
        return {
          url: "complete_registration",
          method: "POST",
          body: formData,
        };
      },
    }),
    recoverPassword: builder.mutation<void, Partial<LoginPayloadModel>>({
      query: (payload: Partial<LoginPayloadModel>) => {
        const formData = objectToFormData(payload);
        return {
          url: "password/reset",
          method: "POST",
          body: formData,
        };
      },
    }),
    restorePassword: builder.mutation<void, RestorePasswordModel>({
      query: (payload: RestorePasswordModel) => {
        const formData = objectToFormData(payload);
        return {
          url: "password/restore",
          method: "POST",
          body: formData,
        };
      },
    }),
    getResetPasswordUserInfo: builder.query<
      ResetPasswordUserInfoResponse,
      string
    >({
      query: (token) => ({
        url: `password/reset/user-info?token=${token}`,
      }),
    }),
  }),
});

// Export hooks for usage in functional components, which are
// auto-generated based on the defined endpoints
export const {
  useLoginMutation,
  useRegisterMutation,
  useCompleteRegistrationMutation,
  useRecoverPasswordMutation,
  useRestorePasswordMutation,
  useGetResetPasswordUserInfoQuery,
} = authApi;
